import {
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  InputGroup,
  InputRightAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Currency,
  type MinimalOrgDto,
  OrganizationType,
  type ViewDocumentAsOwnerDto,
  useAwardWithoutCompetitionMutation,
  type WinnerDocumentationRule,
} from "../../../../../autogen/bff-api";
import { useApiError } from "../../../../../common/errors/useApiError";
import { NumericInput } from "../../../../../common/input/NumericInput";
import { OrganizationSelector } from "../../../../../common/organization/OrganizationSelector";
import { WinnerDocumentationStatementFields } from "../../../../sourcing-event/award/WinnerDocumentationStatement";
import { documentationNoteTooLong } from "../../../../sourcing-event/award/DocumentationNoteField";

interface Props {
  eventId: string;
  requiresDocumentationCheck?: boolean;
  offersDocumentationCheck?: boolean;
  documentationRule?: WinnerDocumentationRule;
  documents: readonly ViewDocumentAsOwnerDto[];
  isOpen: boolean;
  onClose: () => void;
  onAwarded: () => void;
}

export const DirectAwardModal = ({
  eventId,
  requiresDocumentationCheck = false,
  offersDocumentationCheck = requiresDocumentationCheck,
  documentationRule,
  documents,
  isOpen,
  onClose,
  onAwarded,
}: Props) => {
  const { t } = useTranslation();
  const apiError = useApiError();
  const [award, { isLoading }] = useAwardWithoutCompetitionMutation();
  const [supplier, setSupplier] = useState<MinimalOrgDto | undefined>();
  const [offerDocumentIds, setOfferDocumentIds] = useState<string[]>([]);
  const [contractValue, setContractValue] = useState<number | null>(null);
  const [documentationCheckedFor, setDocumentationCheckedFor] = useState<string | null>(null);
  const [documentationNote, setDocumentationNote] = useState("");
  const documentationChecked = supplier != null && documentationCheckedFor === supplier.id;

  const toggleDocument = (id: string, chosen: boolean) =>
    setOfferDocumentIds((current) => (chosen ? [...current, id] : current.filter((existing) => existing !== id)));

  const submit = async () => {
    if (!supplier) return;
    try {
      await award({
        eventId,
        awardWithoutCompetitionRequest: {
          supplierOrganizationId: supplier.id,
          offerDocumentIds,
          contractValue: contractValue != null ? { value: contractValue, currency: Currency.Nok } : null,
          ...(offersDocumentationCheck && documentationChecked
            ? { winnerDocumentationCheck: { note: documentationNote.trim() || null } }
            : {}),
        },
      }).unwrap();
      onAwarded();
    } catch (e) {
      apiError.trigger(e);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Award the contract")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="smMuted" mb="5">
            {t("The procurement is awarded straight to the supplier you choose. There is no tender period and no standstill.")}
          </Text>
          <FormControl isRequired mb="5">
            <FormLabel fontSize="sm">{t("Supplier")}</FormLabel>
            <OrganizationSelector
              organization={supplier}
              type={OrganizationType.Organization}
              onChange={({ organization }) => setSupplier(organization)}
            />
            <FormHelperText>{t("Only companies registered in SourceMagnet can be chosen.")}</FormHelperText>
          </FormControl>
          <FormControl mb="5">
            <FormLabel fontSize="sm">{t("The supplier's offer")}</FormLabel>
            {documents.length === 0 ? (
              <Text fontSize="sm" color="smMuted">
                {t("Upload the offer or price quote in the Documents step to attach it here.")}
              </Text>
            ) : (
              <Stack spacing="2">
                {documents.map((document) => (
                  <Checkbox
                    key={document.id}
                    colorScheme="teal"
                    isChecked={offerDocumentIds.includes(document.id)}
                    onChange={(e) => toggleDocument(document.id, e.target.checked)}
                  >
                    <Text fontSize="sm">{document.name}</Text>
                  </Checkbox>
                ))}
              </Stack>
            )}
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">{t("Contract value")}</FormLabel>
            <InputGroup maxW="280px">
              <NumericInput numericValue={contractValue} placeholder="0" onNumericChange={setContractValue} />
              <InputRightAddon>{t("NOK")}</InputRightAddon>
            </InputGroup>
            <FormHelperText>{t("Excluding VAT. The contract award notice states it.")}</FormHelperText>
          </FormControl>
          {offersDocumentationCheck && supplier && (
            <WinnerDocumentationStatementFields
              suppliers={[supplier.name]}
              rule={documentationRule}
              checked={documentationChecked}
              onCheckedChange={(checked) => setDocumentationCheckedFor(checked ? supplier.id : null)}
              note={documentationNote}
              onNoteChange={setDocumentationNote}
            />
          )}
        </ModalBody>
        <ModalFooter gap="3">
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            colorScheme="teal"
            onClick={submit}
            isLoading={isLoading}
            isDisabled={
              !supplier ||
              (requiresDocumentationCheck && !documentationChecked) ||
              (offersDocumentationCheck && documentationChecked && documentationNoteTooLong(documentationNote))
            }
          >
            {t("Award the contract")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
